import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  Check as SaveIcon,
  Close as CancelIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import WYSIWYGRichTextEditor from '../page-builder/WYSIWYGRichTextEditor';

export interface InlineEditFieldProps {
  value: string;
  onSave: (newValue: string) => Promise<void>;
  canEdit: boolean;
  type: 'text' | 'richtext';
  placeholder?: string;
  typographyVariant?: 'h4' | 'h5' | 'body1';
}

const InlineEditField: React.FC<InlineEditFieldProps> = ({
  value,
  onSave,
  canEdit,
  type,
  placeholder,
  typographyVariant = 'body1',
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const startEdit = useCallback(() => {
    if (!canEdit || editing) return;
    setDraft(value);
    setEditing(true);
  }, [canEdit, editing, value]);

  const handleSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (err: any) {
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, saving]);

  const handleCancel = useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [value]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (type === 'text') {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSave();
      } else if (e.key === 'Escape') {
        handleCancel();
      }
    } else {
      if (e.key === 'Escape') {
        handleCancel();
      }
    }
  }, [type, handleSave, handleCancel]);

  // Render save/cancel action buttons
  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
      <Tooltip title="Save (Enter)">
        <span>
          <IconButton
            size="small"
            color="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Cancel (Esc)">
        <IconButton size="small" onClick={handleCancel} disabled={saving}>
          <CancelIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );

  if (!editing) {
    return (
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          display: 'inline-block',
          width: '100%',
          '&:hover .inline-edit-icon': canEdit ? { opacity: 1 } : {},
        }}
      >
        {type === 'text' ? (
          <Typography
            variant={typographyVariant}
            sx={{
              cursor: 'default',
              borderRadius: 1,
              px: 0.5,
              wordBreak: 'break-word',
              overflowWrap: 'anywhere',
            }}
          >
            {value || <span style={{ color: 'rgba(0,0,0,0.38)' }}>{placeholder}</span>}
          </Typography>
        ) : (
          <Box
            sx={{
              cursor: 'default',
              borderRadius: 1,
              px: 0.5,
            }}
          >
            {value ? (
              <div
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value) }}
              />
            ) : (
              <Typography variant={typographyVariant} color="text.disabled">
                {placeholder}
              </Typography>
            )}
          </Box>
        )}

        {canEdit && (
          <Tooltip title="Edit">
            <IconButton
              className="inline-edit-icon"
              size="small"
              onClick={startEdit}
              sx={{
                position: 'absolute',
                top: 0,
                right: -36,
                opacity: 0,
                transition: 'opacity 0.15s',
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Snackbar
          open={!!error}
          autoHideDuration={5000}
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Edit mode
  return (
    <Box onKeyDown={handleKeyDown}>
      {type === 'text' ? (
        <>
          <TextField
            autoFocus
            fullWidth
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={saving}
            variant="outlined"
            size="small"
            inputProps={{
              style: {
                fontSize: typographyVariant === 'h4' ? '2.125rem' : typographyVariant === 'h5' ? '1.5rem' : '1rem',
                fontWeight: typographyVariant === 'h4' || typographyVariant === 'h5' ? 600 : 400,
              }
            }}
          />
          {actionButtons}
        </>
      ) : (
        <>
          <WYSIWYGRichTextEditor
            value={draft}
            onChange={setDraft}
            placeholder={placeholder}
            minHeight={200}
            readonly={saving}
          />
          {actionButtons}
        </>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InlineEditField;
